// =============================================================================
// src/features/copilot-chat/copilot-dataset.ts
// Dataset canônico do chat do Copiloto IA (números fictícios consistentes).
//
// Proveniencia: porte FIEL de assets/copilot-chat.js linhas 57-127 (`var DATA`
//   + `var THREAT_CLASS`). Este DATA e LOCAL ao chat e NAO tem relacao com o
//   `var DATA` politico de assets/unified-app.js (esse vive em src/data) — por
//   isso fica no proprio modulo, com tipos explicitos (CLAUDE.md regra 5).
//
// Nomes greppaveis: `CopilotChatDataset`/`COPILOT_CHAT_DATA`/`COPILOT_THREAT_CLASS`
//   no lugar do generico `DATA`. SRP: so dados, sem DOM, sem timers.
// =============================================================================

// Banda qualitativa do índice de reputação (irm.band). Reusa a união canônica de
// reputation-data (era o literal 'Bom' do mock estático; agora aceita as 4 faixas reais).
import type { ReputationBandName } from '../reputation/reputation-data';
export type ReputationBand = ReputationBandName;

/** Subdimensão do índice (Percepção/Presença/Protagonismo). */
export interface CopilotSubdim {
  readonly label: string;
  readonly val: number;
}

/** Índice de Reputação do Mandato (DATA.irm). */
export interface CopilotIndexData {
  readonly value: number;
  readonly band: ReputationBand;
  /** Variação em pontos nas últimas 24h (negativa = queda). */
  readonly trend: number;
  readonly subdims: readonly CopilotSubdim[];
}

/** Bloco de nível de ameaça (DATA.threatGeral / DATA.threatTema). */
export interface CopilotThreatData {
  /** Nível 1..5 (indexa COPILOT_THREAT_CLASS). */
  readonly level: number;
  readonly label: string;
  readonly note: string;
}

/** Quebra de sentimento das menções em 24h (DATA.sentimento). */
export interface CopilotSentimentData {
  readonly neg: number;
  readonly neu: number;
  readonly pos: number;
  readonly total: number;
  readonly foot: string;
}

/** Classe de sentimento de uma ocorrência do feed. */
export type FeedSentiment = 'neg' | 'neu' | 'pos';

/** Ocorrência datada no feed de monitoramento (DATA.feed[*]). */
export interface CopilotFeedItem {
  readonly time: string;
  readonly src: string;
  readonly sent: FeedSentiment;
  /** Nível de ameaça 1..5 da ocorrência. */
  readonly threat: number;
  readonly text: string;
  readonly tags: readonly string[];
}

/** Diagnóstico fechado do Copiloto (DATA.diagnostico). */
export interface CopilotDiagnostico {
  readonly contexto: string;
  readonly impacto: string;
  readonly leitura: string;
}

/** Tipo de stakeholder monitorado (DATA.stakeholders[*].kind). */
export type StakeholderKind = 'risk' | 'watch' | 'opp';

/** Stakeholder monitorado (DATA.stakeholders[*]). */
export interface CopilotStakeholder {
  readonly name: string;
  readonly kind: StakeholderKind;
}

/** Ação do plano humano-no-loop (DATA.plano[*]). */
export interface CopilotPlanAction {
  /** Janela ('até 12h', 'gatilho', etc.) — 'gatilho' marca ação de contingência. */
  readonly when: string;
  readonly text: string;
  readonly tags: readonly string[];
}

/** Série da evolução temporal do índice (DATA.evolucao). */
export interface CopilotEvolucaoData {
  readonly points: readonly number[];
  readonly labels: readonly string[];
  readonly axis: readonly string[];
  readonly min: number;
  readonly max: number;
  /** Índice do ponto onde cai a linha tracejada do recorte de 18s. */
  readonly markerIdx: number;
}

/** Linha do ranking de Share of Voice (DATA.shareOfVoice[*]). */
export interface CopilotShareRow {
  readonly name: string;
  readonly pct: number;
  /** True para o próprio mandato (destaque visual). */
  readonly self?: boolean;
}

/** Direção do impacto num item da linha do tempo. */
export type TimelineImpactDir = 'up' | 'down';

/** Impacto numérico opcional de um marco da linha do tempo. */
export interface CopilotTimelineImpact {
  readonly dir: TimelineImpactDir;
  readonly val: string;
}

/** Sentimento/estado de um marco da linha do tempo (DATA.linhaTempo[*].sent). */
export type TimelineSentiment = 'neg' | 'neu' | 'pos' | 'future';

/** Marco da linha do tempo da narrativa (DATA.linhaTempo[*]). */
export interface CopilotTimelineEvent {
  readonly when: string;
  readonly title: string;
  readonly sent: TimelineSentiment;
  readonly desc: string;
  readonly impact?: CopilotTimelineImpact;
}

/** Barra de volume por canal (DATA.volumeCanal[*]). */
export interface CopilotVolumeBar {
  readonly label: string;
  readonly val: number;
  /** True para o canal de pico (badge "pico"). */
  readonly peak?: boolean;
}

/** Forma completa do dataset canônico do chat (porte de `var DATA`). */
export interface CopilotChatDataset {
  readonly irm: CopilotIndexData;
  readonly threatGeral: CopilotThreatData;
  readonly threatTema: CopilotThreatData;
  readonly sentimento: CopilotSentimentData;
  readonly feed: readonly CopilotFeedItem[];
  readonly diagnostico: CopilotDiagnostico;
  readonly stakeholders: readonly CopilotStakeholder[];
  readonly plano: readonly CopilotPlanAction[];
  readonly evolucao: CopilotEvolucaoData;
  readonly shareOfVoice: readonly CopilotShareRow[];
  readonly linhaTempo: readonly CopilotTimelineEvent[];
  readonly volumeCanal: readonly CopilotVolumeBar[];
}

/**
 * Dataset canônico do chat. Valores VERBATIM de assets/copilot-chat.js (57-125):
 *   índice 53,1 (faixa Bom, −2,4 pts), ameaça geral nível 2 e do tema nível 3,
 *   1.240 menções (47/38/15), 6 ocorrências, 5 ações de plano. Números batem com
 *   o glossário do plano — constraint de paridade, NAO ajustar.
 */
export const COPILOT_CHAT_DATA: CopilotChatDataset = {
  irm: {
    value: 53.1,
    band: 'Bom',
    trend: -2.4,
    subdims: [
      { label: 'Percepção', val: 58 },
      { label: 'Presença', val: 49 },
      { label: 'Protagonismo', val: 61 },
    ],
  },
  threatGeral: {
    level: 2,
    label: 'Contido',
    note: 'Movimento contido e reversível no dia. Gatilho de elevação: entrada de veículo de alcance estadual ou repercussão do pedido de informação como pauta própria no Senado.',
  },
  threatTema: {
    level: 3,
    label: 'Ativo e localizado',
    note: 'Vetor principal: recorte de 18s fora de contexto somado ao pedido de informação da oposição. A favor: edital e cronograma públicos no Portal da Transparência; base orgânica reagindo (+11% hoje).',
  },
  sentimento: {
    neg: 47,
    neu: 38,
    pos: 15,
    total: 1240,
    foot: '71% concentrado na Zona Leste · pico às 08h40 (publicação da matéria)',
  },
  feed: [
    { time: '07h12', src: 'Portal Amazonas Agora', sent: 'neg', threat: 3, text: '"Contrato do pronto-socorro da Zona Leste sob questionamento."', tags: ['Imprensa', 'ZonaLeste', 'Obra'] },
    { time: '08h40', src: '@oposicao_local', sent: 'neg', threat: 4, text: '"Cadê a explicação sobre o valor da obra do pronto-socorro?"', tags: ['Oposição', 'Senado', 'PedidoDeInformação'] },
    { time: '09h05', src: 'Liderança aliada', sent: 'pos', threat: 1, text: 'Defende transparência e cita o Portal da Transparência do mandato.', tags: ['Base', 'Aliados'] },
    { time: '09h22', src: '@base_zonaleste', sent: 'pos', threat: 1, text: '"Finalmente um mandato que mostra os números da obra."', tags: ['Eleitorado', 'ZonaLeste'] },
    { time: '10h05', src: 'Diário do Amazonas', sent: 'neu', threat: 2, text: 'Compara a reputação dos parlamentares no semestre.', tags: ['Imprensa', 'Benchmark'] },
    { time: '11h20', src: 'Agenda Zona Leste', sent: 'pos', threat: 1, text: 'Repercute bem em veículos e perfis locais.', tags: ['Eleitorado', 'Agenda'] },
  ],
  diagnostico: {
    contexto: 'Matéria sobre o contrato da reforma do pronto-socorro da Zona Leste, amplificada por um recorte de 18s sem o edital, e potencializada pelo pedido de informação da oposição no Senado.',
    impacto: '−2,4 pts no índice em 24h (queda na dimensão Percepção); 1.240 menções, 47% negativas, 71% na Zona Leste. Impacto contido e reversível no dia.',
    leitura: 'Crise de contexto, não de fato — o ponto atacado (valor da obra) tem resposta documental pública. O risco real é de duração, não de mérito.',
  },
  stakeholders: [
    { name: 'Eleitorado da Zona Leste', kind: 'risk' },
    { name: 'Imprensa local', kind: 'risk' },
    { name: 'Oposição no Senado', kind: 'watch' },
    { name: 'Base e aliados', kind: 'opp' },
    { name: 'Apoiadores institucionais', kind: 'watch' },
  ],
  plano: [
    { when: 'até 12h', text: 'Publicar nota factual de esclarecimento com link direto ao edital e ao cronograma no Portal da Transparência, no canal de maior alcance na Zona Leste.', tags: ['Eleitorado ZL'] },
    { when: 'até 12h30', text: 'Municiar três lideranças aliadas com os mesmos dados de transparência para amplificação orgânica coordenada.', tags: ['Base'] },
    { when: 'até 13h', text: 'Oferecer entrevista/nota técnica exclusiva a um veículo de referência para fixar a versão correta antes do ciclo da tarde.', tags: ['Imprensa'] },
    { when: 'até 13h30', text: 'Enviar resumo proativo de andamento e próximos passos aos apoiadores institucionais.', tags: ['Institucionais'] },
    { when: 'gatilho', text: 'Se o tema chegar à TV local OU o pedido de informação virar pauta própria no Senado, elevar o porta-voz para o próprio senador e ativar dossiê de 1 página.', tags: ['Contingência'] },
  ],
  evolucao: {
    points: [56, 57, 58, 57, 56, 57, 56, 57, 55, 56, 57, 51, 53],
    labels: ['27/02', '06/03', '13/03', '20/03', '27/03', '03/04', '10/04', '17/04', '24/04', '01/05', '08/05', '15/05', '22/05'],
    axis: ['Fev', 'Mar', 'Abr', 'Mai'],
    min: 44,
    max: 62,
    markerIdx: 11,
  },
  shareOfVoice: [
    { name: 'Omar Aziz', pct: 34, self: true },
    { name: 'Oposição (líder)', pct: 22 },
    { name: 'Aliado de base', pct: 14 },
    { name: 'Outros parlamentares', pct: 30 },
  ],
  linhaTempo: [
    { when: '27 fev', title: 'Edital publicado', sent: 'neu', desc: 'O contrato da reforma do pronto-socorro entra no Portal da Transparência do mandato.' },
    { when: '12 mai · 07h12', title: 'Matéria + recorte de 18s', sent: 'neg', desc: 'O Portal Amazonas Agora questiona o valor da obra; um recorte de 18s viraliza sem o edital.', impact: { dir: 'down', val: '−2,4 pts' } },
    { when: '12 mai · 08h40', title: 'Pedido de informação no Senado', sent: 'neg', desc: 'A oposição protocola pedido e mantém o tema em pauta no ciclo do dia.' },
    { when: 'hoje · 14h', title: 'Nota factual + amplificação', sent: 'pos', desc: 'Resposta ancorada no edital, veiculada na Zona Leste antes do ciclo da tarde.', impact: { dir: 'up', val: '+1,8 proj.' } },
    { when: 'hoje · 14h30', title: 'Reavaliação do índice', sent: 'future', desc: 'Recálculo automático do Índice de Reputação para decidir desescalada.' },
  ],
  volumeCanal: [
    { label: 'Portais', val: 612, peak: true },
    { label: 'Redes sociais', val: 410 },
    { label: 'Rádio / TV', val: 140 },
    { label: 'Impresso', val: 78 },
  ],
};

/**
 * Nível de ameaça (1..5) → classe CSS do card (porte de `var THREAT_CLASS`, 127).
 * Níveis 1-2 = low, 3 = mid, 4-5 = high.
 */
export const COPILOT_THREAT_CLASS: Record<number, string> = {
  1: 'cop-threat--low',
  2: 'cop-threat--low',
  3: 'cop-threat--mid',
  4: 'cop-threat--high',
  5: 'cop-threat--high',
};
